import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Package, Calendar, Search, Filter, Download, Globe, Store, 
  ChevronDown, ChevronUp, Phone, MapPin, CreditCard, Clock,
  CheckCircle, Truck, AlertCircle, Lock, FileSpreadsheet
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  realizado: { label: 'Pedido Realizado', color: 'bg-blue-100 text-blue-800', icon: Package },
  en_envio: { label: 'Em Envio', color: 'bg-yellow-100 text-yellow-800', icon: Truck },
  pagado: { label: 'Pago', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cerrado: { label: 'Fechado', color: 'bg-gray-100 text-gray-800', icon: Lock },
};

const PAYMENT_METHODS = {
  pix: { label: 'PIX', color: 'bg-purple-100 text-purple-800' },
  dinheiro: { label: 'Dinheiro', color: 'bg-green-100 text-green-800' },
  cartao: { label: 'Cartão', color: 'bg-blue-100 text-blue-800' },
  entrega: { label: 'Na Entrega', color: 'bg-orange-100 text-orange-800' },
};

export const OrdersModule = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    source: 'all',
    startDate: '',
    endDate: ''
  });
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [filters]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let url = `${API}/admin/orders?`;
      
      if (filters.source && filters.source !== 'all') {
        url += `source=${filters.source}&`;
      }
      if (filters.startDate) {
        url += `start_date=${filters.startDate}&`;
      }
      if (filters.endDate) {
        url += `end_date=${filters.endDate}&`;
      }
      
      const response = await axios.get(url, { headers });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderNumber, newStatus) => {
    if (newStatus === 'cerrado') {
      setShowCloseConfirm({ orderNumber, newStatus });
      return;
    }
    
    await performStatusUpdate(orderNumber, newStatus);
  };

  const performStatusUpdate = async (orderNumber, newStatus) => {
    setUpdatingStatus(orderNumber);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/orders/${orderNumber}/status`, 
        { status: newStatus }, 
        { headers }
      );
      toast.success(`Status atualizado para "${STATUS_CONFIG[newStatus]?.label}"`);
      fetchOrders();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar status');
    } finally {
      setUpdatingStatus(null);
      setShowCloseConfirm(null);
    }
  };

  const exportToExcel = () => {
    if (!orders.length) {
      toast.error('Nenhum pedido para exportar');
      return;
    }

    const headers = ['Número', 'Data', 'Origem', 'Cliente', 'Telefone', 'Endereço', 'Produtos', 'Total', 'Pagamento', 'Status'];
    const rows = orders.map(order => {
      const date = order.created_at ? new Date(order.created_at).toLocaleString('pt-BR') : '';
      const products = order.items?.map(i => `${i.quantity}x ${i.name}`).join('; ') || '';
      return [
        order.order_number || order.sale_number,
        date,
        order.source === 'pdv' ? 'PDV' : 'Web',
        order.customer_name || '',
        order.customer_phone || '',
        order.address || '',
        products,
        `R$ ${(order.total || 0).toFixed(2)}`,
        PAYMENT_METHODS[order.payment_method]?.label || order.payment_method,
        STATUS_CONFIG[order.status]?.label || order.status
      ];
    });

    const csvContent = [headers, ...rows].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = filters.startDate && filters.endDate 
      ? `${filters.startDate}_${filters.endDate}` 
      : new Date().toISOString().split('T')[0];
    link.download = `pedidos_armazem88_${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Relatório exportado com sucesso!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  // Stats
  const totalOrders = orders.length;
  const webOrders = orders.filter(o => o.source === 'web').length;
  const pdvOrders = orders.filter(o => o.source === 'pdv').length;
  const totalRevenue = orders.reduce((acc, o) => acc + (o.total || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Pedidos</h2>
          <p className="text-muted-foreground">Gerencie todos os pedidos da loja</p>
        </div>
        <Button onClick={exportToExcel} variant="outline" className="flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4" />
          Exportar Excel
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-xl border p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[150px]">
            <Label className="text-xs text-muted-foreground">Origem</Label>
            <Select
              value={filters.source}
              onValueChange={(value) => setFilters(prev => ({ ...prev, source: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="web">Web</SelectItem>
                <SelectItem value="pdv">PDV</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <Label className="text-xs text-muted-foreground">Data Início</Label>
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </div>
          <div className="flex-1 min-w-[150px]">
            <Label className="text-xs text-muted-foreground">Data Fim</Label>
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
          <Button variant="outline" onClick={() => setFilters({ source: 'all', startDate: '', endDate: '' })}>
            Limpar
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border p-4">
          <p className="text-sm text-muted-foreground">Total de Pedidos</p>
          <p className="text-2xl font-black">{totalOrders}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-600 flex items-center gap-1">
            <Globe className="w-4 h-4" /> Pedidos Web
          </p>
          <p className="text-2xl font-black text-blue-700">{webOrders}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm text-green-600 flex items-center gap-1">
            <Store className="w-4 h-4" /> Vendas PDV
          </p>
          <p className="text-2xl font-black text-green-700">{pdvOrders}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <p className="text-sm text-purple-600">Receita Total</p>
          <p className="text-2xl font-black text-purple-700">R$ {totalRevenue.toFixed(2)}</p>
        </div>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando pedidos...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-xl border">
            <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhum pedido encontrado</p>
          </div>
        ) : (
          orders.map(order => {
            const isExpanded = expandedOrder === order.order_number;
            const StatusIcon = STATUS_CONFIG[order.status]?.icon || Package;
            const isClosed = order.status === 'cerrado';
            
            return (
              <div 
                key={order.order_number || order.order_id} 
                className={`bg-card rounded-xl border overflow-hidden ${isClosed ? 'opacity-75' : ''}`}
              >
                {/* Order Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setExpandedOrder(isExpanded ? null : order.order_number)}
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${order.source === 'pdv' ? 'bg-green-100' : 'bg-blue-100'}`}>
                        {order.source === 'pdv' ? (
                          <Store className="w-5 h-5 text-green-600" />
                        ) : (
                          <Globe className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-bold">{order.order_number || order.sale_number}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_CONFIG[order.status]?.color || 'bg-gray-100'}`}>
                        <StatusIcon className="w-3 h-3 inline mr-1" />
                        {STATUS_CONFIG[order.status]?.label || order.status}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${PAYMENT_METHODS[order.payment_method]?.color || 'bg-gray-100'}`}>
                        {PAYMENT_METHODS[order.payment_method]?.label || order.payment_method}
                      </span>
                      <span className="font-black text-lg">R$ {(order.total || 0).toFixed(2)}</span>
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>
                </div>

                {/* Order Details (Expanded) */}
                {isExpanded && (
                  <div className="border-t p-4 bg-muted/30">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Customer Info */}
                      <div className="space-y-3">
                        <h4 className="font-bold text-sm">Informações do Cliente</h4>
                        <div className="space-y-2 text-sm">
                          <p className="flex items-center gap-2">
                            <span className="text-muted-foreground">Cliente:</span>
                            <span className="font-medium">{order.customer_name || 'Não informado'}</span>
                          </p>
                          <p className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <span>{order.customer_phone || 'Não informado'}</span>
                          </p>
                          <p className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                            <span>{order.address || 'Não informado'}</span>
                          </p>
                        </div>
                      </div>

                      {/* Products */}
                      <div className="space-y-3">
                        <h4 className="font-bold text-sm">Produtos</h4>
                        <div className="space-y-2">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm bg-white p-2 rounded">
                              <span>{item.quantity}x {item.name}</span>
                              <span className="font-medium">R$ {((item.price || 0) * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Status Change (only for web orders that are not closed) */}
                    {order.source === 'web' && !isClosed && (
                      <div className="mt-6 pt-4 border-t">
                        <Label className="text-sm font-bold mb-2 block">Alterar Status</Label>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                            const Icon = config.icon;
                            return (
                              <Button
                                key={key}
                                size="sm"
                                variant={order.status === key ? 'default' : 'outline'}
                                onClick={() => updateOrderStatus(order.order_number, key)}
                                disabled={updatingStatus === order.order_number || order.status === key}
                                className={order.status === key ? 'bg-primary' : ''}
                              >
                                <Icon className="w-4 h-4 mr-1" />
                                {config.label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {isClosed && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Este pedido está fechado e não pode ser modificado.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Close Confirmation Dialog */}
      <AlertDialog open={!!showCloseConfirm} onOpenChange={() => setShowCloseConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Fechar Pedido Permanentemente?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>Atenção!</strong> Após fechar este pedido, não será possível modificá-lo.
              <br /><br />
              Pedido: <strong>{showCloseConfirm?.orderNumber}</strong>
              <br /><br />
              Tem certeza que deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => performStatusUpdate(showCloseConfirm?.orderNumber, 'cerrado')}
              className="bg-red-600 hover:bg-red-700"
            >
              Sim, Fechar Pedido
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
